import { findOne, removeDuplicates } from 'rcd-cpp-workspace/utils/array';
import * as React from 'react';
import { FieldType } from '../enums/FieldType';
import { NodeType } from '../enums/NodeType';
import { Tree } from '../enums/Tree';
import { ItemType } from '../enums/ItemType';
import {
  Metadata,
  Schema,
  SchemaField,
  WorkspaceSetting
} from '../types/Metadata';
import { ContentTypeConfig } from '../types/ContentTypeConfiguration';
import { DataMapping } from '../types/DataMapping';
import {
  clone,
  generateHashCode,
  getDefaultDataValue,
  getIntersection,
  getMetadata,
  getReferenceValue,
  getSchema
} from './index';
import {
  IsHiddenFn,
  Node,
  NodeSetting,
  RenderDataFn,
  TreeSortFn
} from '../components/TreeSelect/treeSelect';
import { AudienceDetailPrefix } from '../enums/AudienceDetailPrefix';

export interface Setting extends NodeSetting {
  field?: SchemaField;
  getValue?: (l: Metadata[]) => Metadata[];
  displayField?: string;
  renderData?: (m: Metadata) => string | React.ReactElement;
  nodeType?: NodeType;
}

export type TreeSettings = {
  name?: string;
  settings: Setting[];
  tree: Tree;
  treeHash?: string;
  treeNodeTypes?: NodeType[];
  primarySettings?: TreeSettings;
  additionalSettings?: TreeSettings;
};

const getRefIds = (list: Metadata[]) =>
  list?.map(l => l?.props?.refId).filter(x => x);

const findByRefIdStartsWith = (startsWith: string) => (list: Metadata[]) =>
  list.filter(l => l.props.refId.startsWith(startsWith));

const findByRefIdList = (refIdList: string[]) => (list: Metadata[]) =>
  list.filter(l => refIdList.includes(l.props.refId));

const findByLabel = (label: NodeType) => (list: Metadata[]) =>
  list.filter(l => l.labels.includes(label));

const getValue = (m: Metadata, f: string) => (m?.props ? m.props[f] : null);

const getSortFn = (schemas: Schema[], nodeType: string | Schema) => {
  if (!nodeType) return null;
  const dataSchema =
    typeof nodeType == 'string' ? getSchema(schemas, nodeType) : nodeType;
  if (!dataSchema) return null;
  const orderField = findOne(dataSchema.props?.fields, {
    isOrderField: true
  });
  if (!orderField) return null;
  const sort: TreeSortFn<Metadata> = (n1, n2) => {
    if (n1.data?.id?.startsWith('n/a--')) return -1;
    if (n2.data?.id?.startsWith('n/a--')) return 1;
    const v1 = getValue(n1.data, orderField.field);
    const v2 = getValue(n2.data, orderField.field);
    if (v1 == v2) return 0;
    if (v1 == null) return -1;
    if (v2 == null) return 1;
    if (typeof v1 == 'string' && typeof v2 == 'string') {
      return v1.localeCompare(v2, 'standard', { sensitivity: 'case' });
    }
    return v2 > v1 ? -1 : 1;
  };
  return sort;
};

const getParents = (
  schemas: Schema[],
  nodeType: string,
  seen: Set<string> = new Set(),
  depth: number = 1
): Schema[][] => {
  const schema = getSchema(schemas, nodeType);
  if (!schema?.props || schema.props.excludeFromTreeView) return [[]];
  const childOfFields = schema.props.fields.filter(
    f => f.fieldType == FieldType.reference && f.relationType == 'CHILD_OF'
  );
  if (!childOfFields?.length || depth >= 5) {
    if (schema) return [[schema]];
    return [[]];
  }

  return childOfFields.reduce((paths, field) => {
    const pathKey = `(${nodeType})-[${field.relationType}]-(${field.dataType})`;
    if (seen.has(pathKey)) return paths;
    seen.add(pathKey);

    const dataType = field.dataType as NodeType;
    getParents(schemas, dataType, new Set(seen), depth + 1).forEach(p => {
      if (!findOne(p, { id: schema.id })) paths.push([...p, schema]);
    });
    return paths;
  }, []);
};

const getCustomTreeSchemas = (
  schemas: Schema[],
  nodeType: string
): Schema[] => {
  const complementaryRelationType = {
    PARENT_OF: 'CHILD_OF'
  };
  schemas?.forEach(schema => {
    const dataType = schema.props?.type;
    schema?.props?.fields.forEach(f => {
      const relationType = complementaryRelationType[f.relationType];
      if (!relationType) return;
      const targetSchema = getSchema(schemas, f.dataType);
      if (
        targetSchema &&
        !findOne(targetSchema.props?.fields, {
          dataType,
          relationType
        })
      ) {
        targetSchema.props.fields.push({
          ...f,
          dataType,
          relationType
        });
      }
    });
  });
  const trees = getParents(schemas, nodeType);
  if (!trees.length) return [];
  const maxLength = Math.max(...trees.map(t => t.length));
  return trees?.filter(t => t.length == maxLength)[0];
};

const getItemAudienceSetting = (
  field: string,
  schemas: Schema[],
  getSchemaField: (f: string) => SchemaField
): TreeSettings => {
  const primaryQuestionSettings: TreeSettings = {
    name: getSchemaField('primaryQuestion')?.name,
    tree: Tree.DemandQuestion,
    settings: [
      {
        hideCheckbox: true,
        sort: getSortFn(schemas, NodeType.RolePriority)
      },
      {
        hideCheckbox: true,
        sort: getSortFn(schemas, NodeType.PriorityJourneyStep)
      },
      {
        depthLimit: getSchemaField('primaryQuestion')?.multiValue
          ? undefined
          : 1,
        field: getSchemaField('primaryQuestion'),
        sort: getSortFn(schemas, NodeType.DemandQuestion),
        getValue: findByLabel(NodeType.DemandQuestion),
        displayField: 'question'
      }
    ]
  };

  const additionalQuestionsSettings: TreeSettings = {
    name: getSchemaField('additionalQuestions')?.name,
    tree: Tree.DemandQuestion,
    settings: [
      {
        hideCheckbox: true,
        sort: getSortFn(schemas, NodeType.RolePriority)
      },
      {
        hideCheckbox: true,
        sort: getSortFn(schemas, NodeType.PriorityJourneyStep)
      },
      {
        field: getSchemaField('additionalQuestions'),
        sort: getSortFn(schemas, NodeType.DemandQuestion),
        getValue: findByLabel(NodeType.DemandQuestion),
        displayField: 'question'
      }
    ]
  };

  const primaryFnRoleSettings: TreeSettings = {
    name: getSchemaField('function')?.treeGroupName,
    tree: Tree.Function,
    settings: [
      {
        depthLimit: 1,
        field: getSchemaField('function'),
        sort: getSortFn(schemas, NodeType.Function),
        getValue: findByRefIdStartsWith('function:'),
        hideCheckbox: true
      },
      {
        depthLimit: 1,
        field: getSchemaField('role'),
        sort: getSortFn(schemas, NodeType.Role),
        getValue: findByRefIdStartsWith('role:')
      }
    ]
  };
  const additionalFnRoleSettings: TreeSettings = {
    name: getSchemaField('additionalFunctions')?.treeGroupName,
    tree: Tree.Function,
    settings: [
      {
        field: getSchemaField('additionalFunctions'),
        sort: getSortFn(schemas, NodeType.Function),
        getValue: findByRefIdStartsWith('function:'),
        hideCheckbox: true
      },
      {
        field: getSchemaField('additionalRoles'),
        sort: getSortFn(schemas, NodeType.Role),
        getValue: findByRefIdStartsWith('role:')
      }
    ]
  };
  const primaryKICTCQSettings: TreeSettings = {
    name: getSchemaField('keyInitiative')?.treeGroupName,
    tree: Tree.KeyInitiative,
    settings: [
      {
        depthLimit: 1,
        field: getSchemaField('keyInitiative'),
        sort: getSortFn(schemas, NodeType.KeyInitiative),
        getValue: findByRefIdStartsWith('ki:')
      },
      {
        field: getSchemaField('coreTopic'),
        sort: getSortFn(schemas, NodeType.CoreTopic),
        getValue: findByRefIdStartsWith('ct:')
      },
      {
        field: getSchemaField('clientQuestion'),
        sort: getSortFn(schemas, NodeType.ClientQuestion),
        getValue: findByRefIdStartsWith('cq:')
      }
    ]
  };
  const additionalKICTCQSettings: TreeSettings = {
    name: getSchemaField('additionalKeyInitiatives')?.treeGroupName,
    tree: Tree.KeyInitiative,
    settings: [
      {
        field: getSchemaField('additionalKeyInitiatives'),
        sort: getSortFn(schemas, NodeType.KeyInitiative),
        getValue: findByRefIdStartsWith('ki:')
      },
      {
        field: getSchemaField('additionalCoreTopics'),
        sort: getSortFn(schemas, NodeType.CoreTopic),
        getValue: findByRefIdStartsWith('ct:')
      },
      {
        field: getSchemaField('additionalClientQuestions'),
        sort: getSortFn(schemas, NodeType.ClientQuestion),
        getValue: findByRefIdStartsWith('cq:')
      }
    ]
  };
  const topicCategorySettings: TreeSettings = {
    name: getSchemaField('topicCategory')?.treeGroupName,
    tree: Tree.TopicCategory,
    settings: [
      {
        depthLimit: 1,
        field: getSchemaField('topicCategory'),
        sort: getSortFn(schemas, NodeType.TopicCategory),
        getValue: findByLabel(NodeType.TopicCategory),
        hideCheckbox: true
      },
      {
        depthLimit: 1,
        field: getSchemaField('taxonomyTag'),
        sort: getSortFn(schemas, NodeType.TaxonomyTag),
        getValue: findByLabel(NodeType.TaxonomyTag)
      }
    ]
  };

  const rolePrioritySetting: TreeSettings = {
    name: getSchemaField('rolePriority')?.treeGroupName,
    tree: Tree.RolePriority,
    settings: [
      {
        field: getSchemaField('rolePriority'),
        sort: getSortFn(schemas, NodeType.RolePriority),
        getValue: findByLabel(NodeType.RolePriority),
        hideCheckbox: true
      },
      {
        field: getSchemaField('priorityJourneyStep'),
        sort: getSortFn(schemas, NodeType.PriorityJourneyStep),
        getValue: findByLabel(NodeType.PriorityJourneyStep)
      }
    ]
  };

  const primaryRolePrioritySetting: TreeSettings = {
    name: getSchemaField('primaryRolePriority')?.treeGroupName,
    tree: Tree.RolePriority,
    settings: [
      {
        field: getSchemaField('primaryRolePriority'),
        sort: getSortFn(schemas, NodeType.RolePriority),
        getValue: findByLabel(NodeType.RolePriority),
        hideCheckbox: true
      },
      {
        field: getSchemaField('primaryPriorityJourneyStep'),
        sort: getSortFn(schemas, NodeType.PriorityJourneyStep),
        getValue: findByLabel(NodeType.PriorityJourneyStep)
      }
    ]
  };

  switch (field) {
    case 'function':
      return {
        ...primaryFnRoleSettings,
        additionalSettings: additionalFnRoleSettings
      };
    case 'additionalFunctions':
      return {
        ...additionalFnRoleSettings,
        primarySettings: primaryFnRoleSettings
      };
    case 'keyInitiative':
      return {
        ...primaryKICTCQSettings,
        additionalSettings: additionalKICTCQSettings
      };
    case 'additionalKeyInitiatives':
      return {
        ...additionalKICTCQSettings,
        primarySettings: primaryKICTCQSettings
      };
    case 'primaryQuestion':
      return {
        ...primaryQuestionSettings,
        additionalSettings: additionalQuestionsSettings
      };
    case 'additionalQuestions':
      return {
        ...additionalQuestionsSettings,
        primarySettings: primaryQuestionSettings
      };
    case 'topicCategory':
      return {
        ...topicCategorySettings
      };
    case 'rolePriority':
      return {
        ...rolePrioritySetting,
        primarySettings: null
      };
    case 'primaryRolePriority':
      return {
        ...primaryRolePrioritySetting,
        additionalSettings: rolePrioritySetting
      };
  }
};

const getTreeSetting = (
  ns: Schema | NodeType,
  schemaField: SchemaField,
  schemas?: Schema[],
  useSchemaFieldOnly?: boolean
): TreeSettings => {
  const schema = typeof ns == 'string' ? getSchema(schemas, ns) : ns;
  let dataType = typeof ns == 'string' ? ns : schema?.props?.type;
  if (schemaField) {
    if (
      ![FieldType.reference, FieldType.custom].includes(schemaField.fieldType)
    )
      return null;
    const getSchemaField = (field: string) =>
      findOne(schema.props.fields, { field });
    dataType = schemaField.dataType as NodeType;
    if (schema.props.type == NodeType.Item) {
      switch (schemaField.field) {
        case 'keyInitiative':
        case 'additionalKeyInitiatives':
        case 'function':
        case 'additionalFunctions':
        case 'topicCategory':
        case 'primaryQuestion':
        case 'additionalQuestions':
        case 'rolePriority':
        case 'primaryRolePriority':
          return getItemAudienceSetting(
            schemaField.field,
            schemas,
            getSchemaField
          );
        case 'stagedPrimaryTopicCategory':
        case 'stagedTopicCategory':
          const stagedChildField = getSchemaField(
            schemaField.field == 'stagedTopicCategory'
              ? 'stagedTaxonomyTag'
              : 'stagedPrimaryTaxonomyTag'
          );
          return {
            name: getSchemaField(schemaField.field)?.treeGroupName,
            tree: Tree.TopicCategory,
            settings: [
              {
                depthLimit: schemaField?.multiValue ? undefined : 1,
                field: getSchemaField(schemaField.field),
                sort: getSortFn(schemas, NodeType.TopicCategory),
                getValue: findByLabel(NodeType.TopicCategory),
                hideCheckbox: true
              },
              {
                depthLimit: stagedChildField?.multiValue ? undefined : 1,
                field: stagedChildField,
                sort: getSortFn(schemas, NodeType.TaxonomyTag),
                getValue: findByLabel(NodeType.TaxonomyTag)
              }
            ]
          };
        case 'stagedRolePriority':
          return {
            name: getSchemaField('stagedRolePriority')?.treeGroupName,
            tree: Tree.RolePriority,
            settings: [
              {
                field: getSchemaField('stagedRolePriority'),
                sort: getSortFn(schemas, NodeType.RolePriority),
                getValue: findByLabel(NodeType.RolePriority),
                hideCheckbox: true
              },
              {
                field: getSchemaField('stagedPriorityJourneyStep'),
                sort: getSortFn(schemas, NodeType.PriorityJourneyStep),
                getValue: findByLabel(NodeType.PriorityJourneyStep)
              }
            ]
          };
        case 'additionalTopicCategory':
          return {
            name: getSchemaField('additionalTopicCategory')?.treeGroupName,
            tree: Tree.TopicCategory,
            settings: [
              {
                field: getSchemaField('additionalTopicCategory'),
                sort: getSortFn(schemas, NodeType.TopicCategory),
                getValue: findByLabel(NodeType.TopicCategory),
                hideCheckbox: true
              },
              {
                field: getSchemaField('additionalTaxonomyTag'),
                sort: getSortFn(schemas, NodeType.TaxonomyTag),
                getValue: findByLabel(NodeType.TaxonomyTag)
              }
            ]
          };
        case 'contentType':
          return {
            name: getSchemaField('contentType')?.treeGroupName,
            tree: Tree.ContentType,
            settings: [
              {
                depthLimit: 1,
                field: getSchemaField('contentType'),
                sort: getSortFn(schemas, NodeType.ContentType),
                getValue: findByRefIdStartsWith('dtyp:'),
                hideCheckbox: d => {
                  const children = getChildren(d);
                  return children && children.length > 0;
                }
              },
              {
                depthLimit: 1,
                field: getSchemaField('contentSubtype'),
                sort: getSortFn(schemas, NodeType.ContentSubtype),
                getValue: findByRefIdStartsWith('dstp:')
              }
            ]
          };
        case 'contextType':
          return {
            name: getSchemaField('contextType')?.name,
            tree: Tree.ContextType,
            settings: [
              {
                depthLimit: 1,
                sort: getSortFn(schemas, NodeType.ContentSubtype)
              },
              {
                depthLimit: 1,
                field: getSchemaField('contextType'),
                sort: getSortFn(schemas, NodeType.ContextType),
                getValue: findByRefIdStartsWith('dctxt:')
              }
            ]
          };
        case 'gtpLayer':
          return {
            name: getSchemaField('gtpLayer')?.treeGroupName,
            tree: Tree.GTP,
            settings: [
              {
                hideCheckbox: true,
                field: getSchemaField('gtpLayer'),
                sort: getSortFn(schemas, NodeType.GTPLayer),
                getValue: findByRefIdStartsWith('gtp_layer:')
              },
              {
                field: getSchemaField('gtpCapability'),
                sort: getSortFn(schemas, NodeType.GTPCapability),
                getValue: findByRefIdStartsWith('gtp_capability:')
              }
            ]
          };
        case 'disrupterTopic': {
          const field = getSchemaField('disrupterTopic');
          return {
            name: field?.name,
            tree: Tree.DisrupterTopic,
            settings: [
              {
                hideCheckbox: true,
                sort: getSortFn(schemas, NodeType.DisrupterGroup)
              },
              {
                field,
                sort: getSortFn(schemas, NodeType.DisrupterTopic),
                getValue: findByRefIdStartsWith('disruptertopic:')
              }
            ]
          };
        }
        case 'gtpCapability':
        case 'role':
        case 'additionalRoles':
        case 'contentSubtype':
        case 'coreTopic':
        case 'clientQuestion':
        case 'additionalCoreTopics':
        case 'additionalClientQuestions':
        case 'stagedCoreTopic':
        case 'stagedAdditionalCoreTopics':
        case 'stagedPriorityJourneyStep':
        case 'stagedTaxonomyTag':
        case 'stagedPrimaryTaxonomyTag':
        case 'priorityJourneyStep':
        case 'primaryPriorityJourneyStep':
        case 'primaryQuestion':
        case 'additionalTaxonomyTag':
        case 'additionalQuestions':
          return { settings: [], tree: null };
      }
      if (useSchemaFieldOnly) return null;
    }
  }
  switch (dataType) {
    case NodeType.Function:
      return {
        tree: Tree.Function,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.Function),
            getValue: findByRefIdStartsWith('function:'),
            nodeType: NodeType.Function
          }
        ]
      };
    case NodeType.Role:
      return {
        tree: Tree.Function,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.Function,
            depthLimit:
              schema?.props?.type == NodeType.DemandQuestion ? 1 : undefined
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.Role),
            getValue: findByRefIdStartsWith('role:'),
            nodeType: NodeType.Role
          }
        ]
      };
    case NodeType.KeyInitiative:
      return {
        tree: Tree.KeyInitiative,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.KeyInitiative),
            getValue: findByRefIdStartsWith('ki:'),
            nodeType: NodeType.KeyInitiative
          }
        ]
      };
    case NodeType.CoreTopic:
      return {
        tree: Tree.KeyInitiative,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.KeyInitiative,
            sort: getSortFn(schemas, NodeType.KeyInitiative)
          },
          {
            nodeType: NodeType.CoreTopic,
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.CoreTopic),
            getValue: findByRefIdStartsWith('ct:')
          }
        ]
      };
    case NodeType.ClientQuestion:
      return {
        tree: Tree.KeyInitiative,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.KeyInitiative,
            sort: getSortFn(schemas, NodeType.KeyInitiative)
          },
          {
            hideCheckbox: true,
            nodeType: NodeType.CoreTopic,
            sort: getSortFn(schemas, NodeType.CoreTopic)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.ClientQuestion,
            sort: getSortFn(schemas, NodeType.ClientQuestion),
            getValue: findByRefIdStartsWith('cq:')
          }
        ]
      };
    case NodeType.ContentType:
      return {
        tree: Tree.ContentType,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.ContentType,
            sort: getSortFn(schemas, NodeType.ContentType),
            getValue: findByRefIdStartsWith('dtyp:')
          }
        ]
      };
    case NodeType.ContentSubtype:
      return {
        tree: Tree.ContentType,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.ContentType,
            sort: getSortFn(schemas, NodeType.ContentType)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.ContentSubtype,
            sort: getSortFn(schemas, NodeType.ContentSubtype),
            getValue: findByRefIdStartsWith('dstp:')
          }
        ]
      };

    case NodeType.ContextType:
      return {
        tree: Tree.ContextType,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.ContentSubtype,
            sort: getSortFn(schemas, NodeType.ContentSubtype)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.ContextType,
            sort: getSortFn(schemas, NodeType.ContextType),
            getValue: findByRefIdStartsWith('dctxt:')
          }
        ]
      };
    case NodeType.GTPLayer:
      return {
        tree: Tree.GTP,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.GTPLayer,
            sort: getSortFn(schemas, NodeType.GTPLayer),
            getValue: findByRefIdStartsWith('gtp_layer:')
          }
        ]
      };
    case NodeType.GTPCapability:
      return {
        tree: Tree.GTP,
        settings: [
          { hideCheckbox: true, sort: getSortFn(schemas, NodeType.GTPLayer) },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.GTPCapability,
            sort: getSortFn(schemas, NodeType.GTPCapability),
            getValue: findByRefIdStartsWith('gtp_capability:')
          }
        ]
      };
    case NodeType.PN:
      return {
        tree: Tree.PN,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.PN),
            getValue: findByRefIdStartsWith('pn:'),
            nodeType: NodeType.PN
          }
        ]
      };
    case NodeType.PNFrame:
      return {
        tree: Tree.PN,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.PN,
            sort: getSortFn(schemas, NodeType.PN)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.PNFrame,
            sort: getSortFn(schemas, NodeType.PNFrame),
            getValue: findByRefIdStartsWith('pnframe:')
          }
        ]
      };
    case NodeType.PNTile:
      return {
        tree: Tree.PN,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.PN,
            sort: getSortFn(schemas, NodeType.PN)
          },
          {
            hideCheckbox: true,
            nodeType: NodeType.PNFrame,
            sort: getSortFn(schemas, NodeType.PNFrame)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.PNTile,
            sort: getSortFn(schemas, NodeType.PNTile),
            getValue: findByRefIdStartsWith('pntile:')
          }
        ]
      };
    case NodeType.DisrupterTopic:
      return {
        tree: Tree.DisrupterTopic,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.DisrupterGroup,
            sort: getSortFn(schemas, NodeType.DisrupterGroup)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.DisrupterTopic),
            getValue: findByRefIdStartsWith('disruptertopic:'),
            nodeType: NodeType.DisrupterTopic
          }
        ]
      };
    case NodeType.RolePriority:
      return {
        tree: Tree.RolePriority,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.RolePriority,
            sort: getSortFn(schemas, NodeType.RolePriority),
            getValue: findByLabel(NodeType.RolePriority)
          }
        ]
      };
    case NodeType.DemandQuestion:
      return {
        tree: Tree.DemandQuestion,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.RolePriority,
            sort: getSortFn(schemas, NodeType.RolePriority)
          },
          {
            hideCheckbox: true,
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.PriorityJourneyStep,
            sort: getSortFn(schemas, NodeType.PriorityJourneyStep),
            getValue: findByLabel(NodeType.PriorityJourneyStep)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.DemandQuestion,
            sort: getSortFn(schemas, NodeType.DemandQuestion),
            getValue: findByLabel(NodeType.DemandQuestion),
            displayField: 'question'
          }
        ]
      };
    case NodeType.PriorityJourneyStep:
      return {
        tree: Tree.RolePriority,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.RolePriority,
            sort: getSortFn(schemas, NodeType.RolePriority)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            nodeType: NodeType.PriorityJourneyStep,
            sort: getSortFn(schemas, NodeType.PriorityJourneyStep),
            getValue: findByLabel(NodeType.PriorityJourneyStep)
          }
        ]
      };
    case NodeType.TaxonomyTag:
      return {
        tree: Tree.TopicCategory,
        settings: [
          {
            hideCheckbox: true,
            nodeType: NodeType.TopicCategory,
            sort: getSortFn(schemas, NodeType.TopicCategory)
          },
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.TaxonomyTag),
            getValue: findByLabel(NodeType.TaxonomyTag),
            nodeType: NodeType.TaxonomyTag
          }
        ]
      };
    case NodeType.TopicCategory:
      return {
        tree: Tree.TopicCategory,
        settings: [
          {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort: getSortFn(schemas, NodeType.TopicCategory),
            getValue: findByLabel(NodeType.TopicCategory),
            nodeType: NodeType.TopicCategory
          }
        ]
      };
    case NodeType.Project:
      return null;
  }

  if (!schemas) return null;

  const treeSchemas = getCustomTreeSchemas(
    clone(schemas),
    schemaField?.dataType || dataType
  );
  if (treeSchemas?.length > 1) {
    return {
      tree: Tree.Custom,
      treeHash: generateHashCode(treeSchemas),
      treeNodeTypes: treeSchemas.map(s => s.props.type),
      settings: treeSchemas.map((s, i) => {
        const sort = getSortFn(schemas, s);
        if (i == treeSchemas.length - 1) {
          return {
            depthLimit: schemaField?.multiValue ? undefined : 1,
            field: schemaField,
            sort,
            getValue: findByLabel(s.props.type),
            displayField: s.props.displayField
          };
        }
        return {
          hideCheckbox: true,
          sort,
          nodeType: s.props.type,
          displayField: s.props.displayField
        };
      })
    };
  }
  return null;
};

const getSelected = (settings: Setting[], data: Partial<Metadata>) => {
  if (!data) return [];
  return settings?.reduce<Metadata[]>((p, s) => {
    const f = s.field?.field;
    if (f && data?.props?.hasOwnProperty(f)) {
      return [...p, ...(data.props[f] || [])];
    }
    return p;
  }, []);
};

const getUniqueKey = (d: Metadata) => d.props?.refId;

const getChildren = (d: Metadata) => d.metadata || [];

const isHidden = (d: Metadata) => {
  if (d.props?.isActive === false) return true;
};

const addToList = (d: Metadata, parents: Metadata[], list: Metadata[]) => {
  const nodes = [...(parents || []), d];
  const newList = [...list];
  for (const n of nodes) {
    if (!n) continue;
    const existed = list.filter(l => getUniqueKey(l) == getUniqueKey(n))[0];
    if (!existed) {
      const node = clone(n);
      node.metadata = [];
      newList.push(node);
    }
  }
  return newList;
};

const flatten = (list: Metadata[]) =>
  list.reduce((p, c) => {
    if (c) p.push(getUniqueKey(c));
    if (c.metadata?.length) return p.concat(flatten(c.metadata));
    return p;
  }, []);

const addNodesToList = (nodes: Metadata[], list: Metadata[]) => {
  let [node, ...children] = nodes;
  if (node) {
    node.metadata = [];
    const existed = list.filter(l => getUniqueKey(l) == getUniqueKey(node))[0];
    if (!existed) {
      list.push(node);
    } else {
      node = existed;
    }
    if (children.length) {
      node.metadata = addNodesToList(children, node.metadata);
    }
  }
  return list;
};

const removeFromList = (d: Metadata, list: Metadata[]): Metadata[] => {
  if (!d || !list?.length) return list;
  const refIdsToRemove = flatten([d]);
  const newList = list?.filter(l => !refIdsToRemove.includes(getUniqueKey(l)));
  return newList;
};

const getDemandQuestionPrefix = (m: Metadata) => {
  if (!m?.labels) return null;
  if (m.props?.refId === 'nonpriorityquestions') {
    return null; // No prefix for the "Perennial Questions" node itself
  }
  for (const label of m.labels) {
    switch (label) {
      case NodeType.RolePriority:
        return AudienceDetailPrefix.MissionCriticalPriority;
      case NodeType.PriorityJourneyStep:
        return AudienceDetailPrefix.JourneyStep;
      case NodeType.DemandQuestion:
        return AudienceDetailPrefix.PerennialQuestion;
      case NodeType.TopicCategory:
        return AudienceDetailPrefix.PerennialPriority;
      case NodeType.TaxonomyTag:
        return AudienceDetailPrefix.PerennialPriority;
      default:
        break;
    }
  }
  return null;
};

const renderData: RenderDataFn<Metadata, Setting> = n => {
  if (!n?.data) return null;
  if (n.setting.renderData) return n.setting.renderData(n.data);
  if (!n.data.props) return null;
  for (const f of [n.setting.displayField, 'name', 'description']) {
    if (f && n.data.props[f]) return n.data.props[f];
  }
};

const getQuestionRefIds = (d: Metadata) => {
  if (!d?.props?.refId) return [];
  if (d.labels?.includes(NodeType.DemandQuestion)) {
    return [d.props.refId];
  }
  const childs = getChildren(d);
  if (!childs?.length) return [];
  return childs.reduce<string[]>((acc, child) => {
    acc.push(...getQuestionRefIds(child));
    return acc;
  }, []);
};

const getTaxonomyTagForQuestion = (
  questionRefId: string,
  topiccatTree: Metadata[]
): Metadata | null => {
  if (!questionRefId || !topiccatTree?.length) return null;

  let foundTaxonomyTag = null;

  // Topiccat tree: TopicCategory -> TaxonomyTag -> DemandQuestion
  for (const topicCategory of topiccatTree) {
    if (
      topicCategory.props?.refId === 'nonpriorityquestions' ||
      topicCategory.props?.refId?.startsWith('nonpriorityquestions')
    ) {
      continue;
    }

    const taxonomyTags = getChildren(topicCategory) || [];
    for (const taxonomyTag of taxonomyTags) {
      const demandQuestions = getChildren(taxonomyTag) || [];
      const matchingQuestion = demandQuestions.find(
        dq => dq.props?.refId === questionRefId
      );

      if (matchingQuestion) {
        foundTaxonomyTag = taxonomyTag;
        break;
      }
    }
    if (foundTaxonomyTag) break;
  }

  return foundTaxonomyTag;
};

const getAllowedContentTypes = (
  ctConfigs: ContentTypeConfig[],
  data: Partial<Metadata>
) => {
  const wfPrioritizationId = getReferenceValue<number>(
    data,
    'workflowPrioritization',
    'id'
  );
  if (!wfPrioritizationId) return null;
  const researchModelId = getReferenceValue<number>(
    data,
    'researchModel',
    'id'
  );
  const configs = ctConfigs.filter(
    c =>
      (!researchModelId || c.props?.modelId == researchModelId) &&
      c.props?.workflowOption?.includes(wfPrioritizationId)
  );
  const allowedCTs = [];
  configs.forEach(({ dstpRefId, dtypRefId }) => {
    if (dstpRefId && !allowedCTs.includes(dstpRefId))
      allowedCTs.push(dstpRefId);
    if (dtypRefId && !allowedCTs.includes(dtypRefId))
      allowedCTs.push(dtypRefId);
  });
  if (!allowedCTs.length) allowedCTs.push(null);
  return allowedCTs;
};

const useSeeAll = (
  workspaceSetting: WorkspaceSetting,
  settings: Setting[],
  data?: Partial<Metadata>,
  mappedDataValue?: string[],
  field?: SchemaField,
  ctConfigs?: ContentTypeConfig[]
) => {
  if (settings == null) return {} as any;
  if (field?.key == 'Item.primaryQuestion') {
    const selectedPrimaryQuestions = getRefIds(getSelected(settings, data));
    const additionalQuestions =
      getRefIds(data?.props?.additionalQuestions) || [];
    const roleRefIds = getRefIds(data?.props?.role) || [];

    const shouldHide: IsHiddenFn<Metadata, Setting> = d => {
      if (additionalQuestions.includes(d.props.refId)) return true;

      if (selectedPrimaryQuestions.length > 0) {
        return true;
      }

      if (!roleRefIds.length) {
        return false;
      }

      const questionRefIds = getQuestionRefIds(d);
      if (getIntersection(questionRefIds, mappedDataValue).length > 0) {
        return false;
      }
      return true;
    };
    const hasNoResults =
      roleRefIds.length > 0 &&
      (!mappedDataValue || mappedDataValue.length == 0);

    return {
      seeOptions: null,
      shouldHide,
      hasNoResults,
      rerenderKey: generateHashCode({
        mappedDataValue,
        selectedRefIds: selectedPrimaryQuestions,
        additionalQuestions,
        roleRefIds,
        hasNoResults
      })
    };
  }
  if (field?.key == 'Item.additionalQuestions') {
    const primaryQuestions = getRefIds(data?.props?.primaryQuestion) || [];
    const additionalRoleRefIds = getRefIds(data?.props?.additionalRoles) || [];
    const selected = getRefIds(data?.props?.additionalQuestions) || [];
    const hasNoResults =
      additionalRoleRefIds.length > 0 &&
      (mappedDataValue == null || mappedDataValue.length === 0);
    const show = (mappedDataValue?.length || hasNoResults) && !selected?.length;
    let seeOptions = show ? ['Less', 'All'] : null;
    const shouldHide: IsHiddenFn<Metadata, Setting> = (
      d,
      parents,
      selectedMap,
      ns
    ) => {
      if (primaryQuestions.includes(d.props.refId)) return true;
      if (hasNoResults) {
        if (seeOptions?.length == 0) return false;
        if (ns.seeOption == 'All') return false;
        return true;
      }
      if (!mappedDataValue?.length) return false;
      if (seeOptions?.length == 0) return false;
      if (ns.seeOption == 'All') return false;
      const questionRefIds = getQuestionRefIds(d).filter(
        qid => !primaryQuestions.includes(qid)
      );
      if (getIntersection(questionRefIds, mappedDataValue).length > 0) {
        return false;
      }
      return true;
    };

    return {
      seeOptions,
      shouldHide,
      rerenderKey: generateHashCode({
        seeOptions,
        mappedDataValue,
        hasNoResults,
        multiValue: true,
        selectedRefIds: selected,
        primaryQuestions,
        additionalRoleRefIds
      })
    };
  }

  if (
    field?.key == 'DemandQuestion.taxonomyTag' ||
    field?.key == 'DemandQuestion.priorityJourneyStep'
  ) {
    const roleRefId = getReferenceValue(data, 'role', 'refId');
    const shouldHide: IsHiddenFn<Metadata, Setting> = d => {
      const associatedRoleRefIds = (d as any).roleRefIds;
      if (roleRefId != null && associatedRoleRefIds != null) {
        return !associatedRoleRefIds.includes(roleRefId);
      }
      return false;
    };
    return {
      seeOptions: null,
      shouldHide,
      rerenderKey: generateHashCode({
        roleRefId,
        selectedRefIds: getRefIds(getSelected(settings, data))
      })
    };
  }

  if (field?.key === 'Item.topicCategory') {
    if (data?.props?.itemType == ItemType.globalSubmission) {
      const selected = getSelected(settings, data);

      const shouldHide: IsHiddenFn<Metadata, Setting> = (d, selectedMap) => {
        const refId = d.props.refId;
        const isTopicCategory = refId?.startsWith('topiccat:');
        const isTaxonomyTag = refId?.startsWith('taxonomytag:');
        if (!mappedDataValue?.length) return false;
        if (selectedMap[refId]) return false;
        if (isTopicCategory) {
          const isInFilter = mappedDataValue.includes(refId);
          if (isInFilter) return false;
          const children = getChildren(d);
          const hasValidChild = children.some(c => {
            const childRefId = c.props.refId;
            return (
              childRefId?.startsWith('taxonomytag:') &&
              mappedDataValue.includes(childRefId)
            );
          });
          if (hasValidChild) return false;
          return true;
        }

        if (isTaxonomyTag) {
          const isInFilter = mappedDataValue.includes(refId);
          if (isInFilter) return false;
          return true;
        }
        return false;
      };

      const hasNoResults = mappedDataValue?.[0] === null;

      return {
        seeOptions: null,
        shouldHide,
        hasNoResults,
        rerenderKey: generateHashCode({
          mappedDataValue,
          selectedRefIds: getRefIds(selected),
          itemType: ItemType.globalSubmission,
          hasNoResults
        })
      };
    }
  }

  if (field?.key === 'Item.rolePriority') {
    if (data?.props?.itemType == ItemType.globalSubmission) {
      const selected = getSelected(settings, data);

      const shouldHide: IsHiddenFn<Metadata, Setting> = (d, selectedMap) => {
        const refId = d.props.refId;

        if (selectedMap[refId]) return false;
        if (mappedDataValue === null) return false;
        if (mappedDataValue.length === 0) return true;
        if (mappedDataValue.includes(refId)) return false;

        return true;
      };

      const hasNoResults =
        mappedDataValue !== null && mappedDataValue.length === 0;

      return {
        seeOptions: null,
        shouldHide,
        hasNoResults,
        rerenderKey: generateHashCode({
          mappedDataValue,
          selectedRefIds: getRefIds(selected),
          itemType: ItemType.globalSubmission,
          hasNoResults
        })
      };
    }
  }

  const selected = getSelected(settings, data);
  let defaultData: string[];
  if (field?.key == 'Item.contentType') {
    const configAllowedCTs = getAllowedContentTypes(ctConfigs, data);
    if (configAllowedCTs != null && mappedDataValue != null) {
      defaultData = mappedDataValue.filter(Boolean);
      if (configAllowedCTs[0] == null || mappedDataValue[0] == null) {
        mappedDataValue = [null];
      } else {
        mappedDataValue = getIntersection(mappedDataValue, configAllowedCTs);
      }
    } else if (mappedDataValue == null) {
      mappedDataValue = configAllowedCTs;
    }
  } else {
    defaultData = getDefaultDataValue(
      workspaceSetting,
      settings?.map(s => s?.field?.dataType || s?.nodeType)
    );
  }
  let multiValue = false;
  if (settings) {
    for (const s of settings) {
      if (!s.hideCheckbox) {
        multiValue = s.field?.multiValue;
        break;
      }
    }
  }

  const show =
    (mappedDataValue?.length || defaultData?.length) &&
    (multiValue || !selected?.length);
  const hasSeeMore =
    !!mappedDataValue?.length &&
    defaultData?.filter(d => !mappedDataValue?.includes(d)).length > 0;

  let seeOptions = null;
  if (show) {
    seeOptions = ['Less'];
    if (hasSeeMore) seeOptions.push('More');
    seeOptions.push('All');
  }

  const shouldHide: IsHiddenFn<Metadata, Setting> = (
    d,
    parents,
    selectedMap,
    ns
  ) => {
    if (seeOptions?.length == 0) return false;
    if (ns.seeOption == 'All') return false;
    const dataToShow = [...(mappedDataValue || [])];
    if (ns.seeOption == 'More' || !mappedDataValue?.length) {
      dataToShow.push(...(defaultData || []));
    }
    if (dataToShow?.length) {
      if (!dataToShow.includes(d.props.refId)) {
        let hidden = false;
        if (
          !parents.filter(p => dataToShow.includes(p.props.refId)).length &&
          (data != null ||
            !getChildren(d).filter(c => dataToShow.includes(c.props.refId))
              .length)
        ) {
          hidden = true;
        } else {
          for (const parent of parents) {
            const children = getChildren(parent);
            if (
              children.filter(child => dataToShow.includes(child.props.refId))
                .length > 0
            ) {
              hidden = true;
              break;
            }
          }
        }
        if (hidden) {
          const ps = [...parents];
          let p = ps.pop();
          while (p != null) {
            if (selectedMap[p.props.refId]) {
              const childRefIds = getRefIds(getChildren(p));
              const hasChildSelected =
                ns.setting.depthLimit != 1
                  ? false
                  : !!childRefIds.filter(c => selectedMap[c]).length;
              if (
                !hasChildSelected &&
                !childRefIds.filter(
                  c => c != d.props.refId && dataToShow.includes(c)
                ).length
              ) {
                hidden = false;
                break;
              }
            }
            p = ps.pop();
          }
        }
        return hidden;
      }
    }
    return false;
  };

  return {
    seeOptions,
    shouldHide,
    rerenderKey: generateHashCode({
      seeOptions,
      defaultData,
      mappedDataValue,
      multiValue,
      selectedRefIds: getRefIds(selected)
    })
  };
};

const getFieldsToUpdate = (
  list: Metadata[],
  settings: Setting[],
  schema: Schema
) =>
  settings.reduce<{ field: SchemaField; value: Metadata[] }[]>((p, s) => {
    if (s.getValue) {
      const f = schema.props.fields.filter(f => f.field == s.field?.field)[0];
      const v = s.getValue(list);
      if (!!f && !!v) p.push({ field: f, value: v });
    }
    return p;
  }, []);

const getImpliedData = ({
  field,
  data,
  disabled,
  settings
}: {
  field: SchemaField;
  data: Partial<Metadata>;
  disabled?: boolean;
  settings: Setting[];
}): Metadata[] | null => {
  if (disabled) return null;
  if (field.fieldType != FieldType.reference || !field.dataType) return null;
  if (field.parentField == 'backtag') return [];
  return settings.reduce<Metadata[]>((arr, setting) => {
    const nodeType = setting.field?.dataType as NodeType;
    if (nodeType && data.metadata) {
      const metadata = getMetadata(data.metadata, nodeType);
      if (metadata?.length) {
        return [...arr, ...metadata];
      }
    }
    return arr;
  }, []);
};

export const filterImplied =
  (
    ws: WorkspaceSetting,
    { settings, primarySettings, additionalSettings }: TreeSettings,
    data: Metadata | Partial<Metadata>
  ) =>
  (impliedNodes: Node<Metadata, TreeSettings>[]) => {
    const showSingleImpliedAsPrimary = ws?.props?.impliedAsPrimary !== false;
    const selected = getSelected(settings, data);

    if (!impliedNodes.length) return impliedNodes;
    const isSingleImplied = impliedNodes.length == 1;
    if (primarySettings && showSingleImpliedAsPrimary) {
      if (isSingleImplied) {
        const pVals = getSelected(primarySettings.settings, data);
        if (!pVals?.length) return [];
      }
    } else if (additionalSettings) {
      if (showSingleImpliedAsPrimary && !selected?.length && isSingleImplied) {
        return impliedNodes;
      }
      return [];
    }
    return impliedNodes;
  };

export const TreeFieldUtils = {
  getTreeSetting,
  getFieldsToUpdate,
  getSelected,
  getImpliedData,
  getChildren,
  getUniqueKey,
  useSeeAll,
  renderData,
  getDemandQuestionPrefix,
  getTaxonomyTagForQuestion,
  filterImplied,
  isHidden,
  addToList,
  addNodesToList,
  removeFromList,
  flatten
};
